import type { Character } from '../domain/character.ts';
import type { PetCompanion } from '../domain/pet.ts';
import type { Item } from '../content/types.ts';
import type { StatBlock } from '../domain/stats.ts';
import { itemEffects, validateInventoryAgainstCatalogue, type InventoryCatalogue } from '../rules/inventory.ts';
import { hotlistAllocation } from '../rules/hotlist.ts';
import { resolveEnhancedStats } from '../rules/enhancedStats.ts';
import { resolveModifiers, statModifierSources, advantageSourcesFor, disadvantageSourcesFor, checkModifiersFor, type CheckContext, type ResolvedModifiers } from '../rules/effects.ts';
import { resolveRollMode, type RollMode } from '../rules/rollModes.ts';

export interface PetEquipment {
  enhancedStats: StatBlock;
  modifiers: ResolvedModifiers;
}

export function petGearInventory(pet: PetCompanion) {
  const counts = new Map<string, number>();
  for (const id of pet.gearItemIds) counts.set(id, (counts.get(id) ?? 0) + 1);
  return [...counts].map(([itemId, quantity]) => ({ itemId, quantity, equipped: quantity, held: 0 }));
}

/** Pets have equipment, not a separate Inventory. Effects never alter base Stats. */
export function buildPetEquipment(character: Character, pet: PetCompanion, lookupItem: (id: string) => Item | undefined): PetEquipment {
  const inventory = petGearInventory(pet).filter((entry) => lookupItem(entry.itemId)?.petGear === true && character.inventory.some((owned) => owned.itemId === entry.itemId && owned.quantity > 0));
  const modifiers = resolveModifiers(itemEffects(inventory, lookupItem));
  return { modifiers, enhancedStats: resolveEnhancedStats(pet.unenhancedStats, statModifierSources(modifiers)) };
}

export function petCheckMode(equipment: PetEquipment | undefined, mode: RollMode, context: CheckContext): RollMode {
  if (!equipment) return mode;
  return resolveRollMode(advantageSourcesFor(equipment.modifiers, context) + (mode === 'advantage' ? 1 : 0), disadvantageSourcesFor(equipment.modifiers, context) + (mode === 'disadvantage' ? 1 : 0));
}

export function petCheckBonuses(equipment: PetEquipment | undefined, context: CheckContext) {
  return equipment ? checkModifiersFor(equipment.modifiers, context).map((m) => ({ label: m.label, value: m.amount })) : [];
}

export function petEquipmentMaxMana(pet: Pick<PetCompanion, 'unenhancedStats' | 'manaBonus'>, equipment?: PetEquipment): number {
  return Math.max(0, (equipment?.enhancedStats.int ?? pet.unenhancedStats.int) + pet.manaBonus + (equipment?.modifiers.maxMana ?? 0));
}

export function copiesOnPets(character: Character, itemId: string): number {
  return character.companions.reduce((sum, pet) => sum + pet.gearItemIds.filter((id) => id === itemId).length, 0);
}

export function setPetEquipment(character: Character, petId: string, itemId: string, desiredCount: boolean | number, catalogue: InventoryCatalogue, now: string): { ok: true; character: Character } | { ok: false; reason: string } {
  const pet = character.companions.find((p) => p.id === petId);
  if (!pet) return { ok: false, reason: 'That companion is no longer attached to this crawler.' };
  const existing = pet.gearItemIds.filter((id) => id === itemId).length;
  const count = typeof desiredCount === 'boolean' ? (desiredCount ? 1 : 0) : desiredCount;
  if (!Number.isInteger(count) || count < 0 || count > 10) return { ok: false, reason: 'Choose 0 to 10 whole copies to equip.' };
  if (existing === count) return { ok: true, character };
  const item = catalogue.lookupItem(itemId);
  if (count > existing) {
    if (!item?.petGear || !item.slot) return { ok: false, reason: 'The GM must designate this Item as specially made pet equipment with a Gear Slot.' };
    const owned = character.inventory.find((entry) => entry.itemId === itemId);
    const free = owned ? owned.quantity - owned.equipped - owned.held - hotlistAllocation(character.hotlist, itemId) - copiesOnPets(character, itemId) : 0;
    if (free < count - existing) return { ok: false, reason: 'No unallocated copy remains in the owning crawler’s Inventory.' };
    const nextPet = { ...pet, gearItemIds: [...pet.gearItemIds.filter((id) => id !== itemId), ...Array<string>(count).fill(itemId)] };
    const problems = validateInventoryAgainstCatalogue(petGearInventory(nextPet), catalogue);
    const enhanced = buildPetEquipment(character, nextPet, catalogue.lookupItem).enhancedStats;
    if (Object.values(enhanced).some((value) => !Number.isInteger(value) || value < 1)) return { ok: false, reason: 'This equipment would take a pet Stat below 1. Ask the GM to review its bonuses.' };
    if (problems.length) return { ok: false, reason: problems.map((p) => p.message).join('; ') };
  }
  const updated = { ...pet, gearItemIds: [...pet.gearItemIds.filter((id) => id !== itemId), ...Array<string>(count).fill(itemId)], updatedAt: now };
  // Removing a Max Mana Item clamps the resource; equipping never refills it.
  updated.currentMana = Math.min(updated.currentMana, petEquipmentMaxMana(updated, buildPetEquipment(character, updated, catalogue.lookupItem)));
  return { ok: true, character: { ...character, companions: character.companions.map((p) => p.id === petId ? updated : p), updatedAt: now } };
}
